// FP3-078C — Contact / Support page (Jun 25, 2026).
//
// Static, frontend-only contact card. No backend, no DB, no form,
// no ticketing system. Single source of truth for "how do I reach
// Frame Practice" — surfaces the support email and a mailto button.
//
// Linked from:
//   - Authenticated sidebar nav (under Help Center)
//   - Public Solo Pilot signup page footer

import { EnvelopeSimple, Headset, ShieldCheck } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import Sidebar from "@/components/layout/Sidebar";
import PageShell from "@/components/layout/PageShell";

const SUPPORT_EMAIL = "admin@frameed.com";
const MAILTO_HREF = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Frame Practice Support")}`;

// FP3-078C — Wrapper that gives authenticated users the standard
// AppLayout shell (sidebar + main column) while keeping the same
// /contact route publicly accessible for the StartPilot footer
// link. Anonymous visitors get the bare card.
export default function Contact() {
  const { user } = useAuth();
  if (user) {
    return (
      <div className="flex min-h-screen bg-[#F9F8F6]" data-testid="app-layout">
        <Sidebar />
        <main className="flex-1 overflow-auto flex flex-col">
          <div className="flex-1">
            <ContactCard authenticated />
          </div>
        </main>
      </div>
    );
  }
  return <ContactCard />;
}

function ContactCard({ authenticated = false }) {
  // FP3-112 — Authenticated in-app view hugs the sidebar via
  // PageShell (px-6, max 1600px, no mx-auto) so the page doesn't
  // sit as a small floating card in a large empty canvas. The
  // support + privacy cards render side-by-side on wide displays
  // (xl+) while stacking on narrower widths. Anonymous visitors
  // arriving from the Solo Pilot signup footer keep the original
  // compact centered card — that surface is a public landing page,
  // not part of the operational workspace.
  const inner = (
    <>
      {/* Header */}
      <div className="flex items-start gap-3 mb-6">
        <div className="mt-1 p-2 rounded-lg bg-[#EFEDE5] text-[#1E3F33]">
          <Headset size={22} weight="duotone" />
        </div>
        <div>
          <h1
            data-testid="contact-page-title"
            className="text-[26px] font-semibold text-[#1E3F33] heading-font tracking-tight"
          >
            Contact Frame Practice
          </h1>
          <p className="text-[13px] text-[#6E6A60] mt-1 max-w-2xl leading-relaxed">
            Questions, issues, or feedback? We&rsquo;d love to hear from you.
          </p>
        </div>
      </div>

      <div
        className={
          authenticated
            ? "grid grid-cols-1 xl:grid-cols-2 gap-4 items-start"
            : "space-y-4"
        }
      >
        {/* Primary support card */}
        <Card
          data-testid="contact-support-card"
          className="border-[#E5E3DB] bg-white"
        >
          <CardHeader className="pb-2 flex flex-row items-start gap-2.5 space-y-0">
            <div className="mt-0.5 p-1.5 rounded-md bg-[#EFEDE5] text-[#1E3F33]">
              <EnvelopeSimple size={18} weight="duotone" />
            </div>
            <div>
              <CardTitle className="text-[15px] font-semibold text-[#1E3F33] heading-font tracking-tight">
                Email support
              </CardTitle>
              <p className="text-[12px] text-[#6E6A60] mt-0.5 leading-snug">
                Reach the Frame Practice team directly.
              </p>
            </div>
          </CardHeader>
          <CardContent className="pt-2 space-y-4">
            <p className="text-[13px] text-[#1F2937] leading-relaxed max-w-[64ch]">
              For questions, bug reports, or product feedback, email us at{" "}
              <a
                href={MAILTO_HREF}
                data-testid="contact-support-email-inline"
                className="text-[#1E3F33] font-medium underline decoration-[#8FA998]/60 underline-offset-2 hover:decoration-[#1E3F33]"
              >
                {SUPPORT_EMAIL}
              </a>
              .
            </p>

            <div>
              <a href={MAILTO_HREF} data-testid="contact-support-email-link">
                <Button
                  type="button"
                  data-testid="contact-support-email-btn"
                  className="h-10 px-4 bg-[#1E3F33] hover:bg-[#173127] text-white rounded-lg text-[13px] font-medium tracking-tight inline-flex items-center gap-2"
                >
                  <EnvelopeSimple size={16} weight="bold" />
                  Email support
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Privacy reminder */}
        <Card
          data-testid="contact-privacy-card"
          className="border-[#E5E3DB] bg-[#FBFAF6]"
        >
          <CardContent className="pt-4 pb-4 flex items-start gap-2.5">
            <div className="mt-0.5 p-1.5 rounded-md bg-[#E8F0EB] text-[#1E3F33]">
              <ShieldCheck size={16} weight="duotone" />
            </div>
            <div>
              <p
                data-testid="contact-privacy-note"
                className="text-[12.5px] text-[#374151] leading-relaxed max-w-[64ch]"
              >
                <span className="font-medium text-[#1E3F33]">Privacy reminder.</span>{" "}
                Please avoid including student names or personally identifiable
                student information in email unless necessary. Screenshots should
                be redacted when possible.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );

  if (authenticated) {
    return (
      <PageShell testId="contact-page" variant="compact" className="animate-none !max-w-[1120px]">
        {inner}
      </PageShell>
    );
  }
  return (
    <div
      data-testid="contact-page"
      className="px-8 py-6 max-w-3xl mx-auto"
    >
      {inner}
    </div>
  );
}
